import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, CheckCircle2, MessageCircle } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Button } from './ui/button';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

// WhatsApp icon SVG
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Obfuscated phone number protection
const getProtectedNumber = () => {
  const p1 = String.fromCharCode(55, 56, 54);
  const p2 = [7, 1, 5].join('');
  const p3 = ((9 * 1000) + (8 * 100) + (3 * 10) + 4).toString();
  const cc = '1';
  return cc + p1 + p2 + p3;
};

// Slider Captcha Component for WhatsApp
const SliderCaptcha = ({ onVerified, verified, resetKey }) => {
  const [position, setPosition] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const threshold = 85;

  useEffect(() => {
    if (!verified) {
      setPosition(0);
    }
  }, [resetKey, verified]);

  const handleMouseDown = (e) => {
    if (verified) return;
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || verified) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const sliderWidth = 44;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, e.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    setPosition(pct);
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [verified, onVerified]);

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      setPosition(prev => prev >= threshold ? 100 : 0);
    }
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleTouchStart = () => {
    if (verified) return;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || verified) return;
    const container = containerRef.current;
    if (!container) return;
    const touch = e.touches[0];
    const rect = container.getBoundingClientRect();
    const sliderWidth = 44;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, touch.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    setPosition(pct);
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging.current && position < threshold) {
      setPosition(0);
    }
    isDragging.current = false;
  };

  return (
    <div className="space-y-2">
      <Label className="text-slate-700 font-medium flex items-center gap-2 text-sm">
        <ShieldCheck className="w-4 h-4 text-[#25D366]" />
        Desliza para verificar
      </Label>
      <div 
        ref={containerRef}
        className={`relative h-11 rounded-xl border-2 transition-colors overflow-hidden select-none ${
          verified 
            ? 'bg-[#25D366]/10 border-[#25D366]' 
            : 'bg-slate-50 border-slate-200'
        }`}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid="wa-slider-captcha"
      >
        <div 
          className={`absolute inset-y-0 left-0 ${
            verified ? 'bg-[#25D366]/20' : 'bg-[#25D366]/10'
          }`}
          style={{ width: `${position}%`, transition: isDragging.current ? 'none' : 'width 0.2s' }}
        />
        
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {verified ? (
            <span className="text-[#25D366] font-semibold flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Verificado
            </span>
          ) : (
            <span className="text-slate-400 text-sm">
              Desliza →
            </span>
          )}
        </div>
        
        <div
          className={`absolute top-1 bottom-1 w-9 rounded-lg flex items-center justify-center shadow-md ${
            verified 
              ? 'bg-[#25D366] cursor-default' 
              : 'bg-[#25D366] hover:bg-[#20bd5a] cursor-grab active:cursor-grabbing'
          }`}
          style={{ 
            left: `calc(${position}% - ${position * 0.35}px + 4px)`,
            transition: isDragging.current ? 'none' : 'left 0.2s'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          data-testid="wa-slider-thumb"
        >
          {verified ? (
            <CheckCircle2 className="w-4 h-4 text-white" />
          ) : (
            <div className="flex gap-0.5">
              <div className="w-0.5 h-3 bg-white/60 rounded-full" />
              <div className="w-0.5 h-3 bg-white/60 rounded-full" />
              <div className="w-0.5 h-3 bg-white/60 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const FloatingButtons = () => {
  const [showWhatsAppVerify, setShowWhatsAppVerify] = useState(false);
  const [notRobot, setNotRobot] = useState(false);
  const [sliderVerified, setSliderVerified] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [chatReady, setChatReady] = useState(false);
  const n8nTriggerRef = useRef(null);

  const isVerified = notRobot && sliderVerified;

  // Load n8n chat and hide its default trigger (no cover hacks)
  useEffect(() => {
    // 1) Inject n8n chat stylesheet
    const link = document.createElement('link');
    link.href = 'https://cdn.jsdelivr.net/npm/@n8n/chat/dist/style.css';
    link.rel = 'stylesheet';
    link.id = 'n8n-chat-css';
    document.head.appendChild(link);

    // 2) Inject CSS to hide n8n default trigger (keep in DOM so we can click it)
    const style = document.createElement('style');
    style.id = 'n8n-custom-styles';
    style.textContent = `
      /* Hide the n8n default floating trigger button visually, but keep it clickable via JS */
      .chat-window-toggle,
      #n8n-chat > button,
      #n8n-chat .chat-window-toggle {
        opacity: 0 !important;
        pointer-events: none !important;
        width: 1px !important;
        height: 1px !important;
        bottom: 0 !important;
        right: 0 !important;
        z-index: -1 !important;
      }

      /* Make the chat window look nice and match the light theme */
      #n8n-chat .chat-window,
      #n8n-chat [class*="chat-window"]:not(.chat-window-toggle) {
        z-index: 9999 !important;
        border-radius: 20px !important;
        box-shadow: 0 25px 50px -12px rgba(0, 102, 204, 0.25) !important;
        overflow: hidden !important;
      }

      /* n8n chat color overrides (light theme w/ brand colors) */
      #n8n-chat {
        --chat--color-primary: #0066CC;
        --chat--color-primary-shade-50: #0052a3;
        --chat--color-primary-shade-100: #003d7a;
        --chat--color-secondary: #2DD4BF;
        --chat--color-secondary-shade-50: #26c4b0;
        --chat--color-white: #ffffff;
        --chat--color-light: #f8fafc;
        --chat--color-light-shade-50: #e2e8f0;
        --chat--color-light-shade-100: #cbd5e1;
        --chat--color-medium: #94a3b8;
        --chat--color-dark: #0f172a;
        --chat--color-typing: #475569;
        --chat--toggle--size: 0px;
      }
    `;
    document.head.appendChild(style);

    // 3) Load n8n chat module and initialize
    let observer = null;
    const initChat = async () => {
      try {
        const module = await import('https://cdn.jsdelivr.net/npm/@n8n/chat/dist/chat.bundle.es.js');
        module.createChat({
          webhookUrl: 'https://n8n-n8n.bnxphm.easypanel.host/webhook/9dc59ad7-df20-46b3-97e4-51b9af2d3902/chat?empresaid=3',
          mode: 'window',
          showWelcomeScreen: false,
          initialMessages: [
            '¡Hola! 👋 Soy el asistente virtual de Tecno Envios Express.',
            '¿En qué puedo ayudarte hoy?'
          ],
          i18n: {
            en: {
              title: 'Tecno Envios Express',
              subtitle: 'Asistente Virtual',
              footer: '',
              getStarted: 'Iniciar conversación',
              inputPlaceholder: 'Escribe tu mensaje...',
              closeButtonTooltip: 'Cerrar chat'
            }
          }
        });

        // Locate the n8n toggle button (it's the only element with .chat-window-toggle)
        const findTrigger = () => {
          const btn = document.querySelector('.chat-window-toggle');
          if (btn) {
            n8nTriggerRef.current = btn;
            setChatReady(true);
            return true;
          }
          return false;
        };

        // Try immediately then observe DOM for the trigger
        if (!findTrigger()) {
          observer = new MutationObserver(() => {
            if (findTrigger() && observer) {
              observer.disconnect();
              observer = null;
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });

          // Safety timeout - stop observing after 15s
          setTimeout(() => {
            if (observer) {
              observer.disconnect();
              observer = null;
            }
          }, 15000);
        }
      } catch (error) {
        console.error('Error loading n8n chat:', error);
      }
    };

    initChat();

    return () => {
      if (observer) observer.disconnect();
      const cssLink = document.getElementById('n8n-chat-css');
      if (cssLink) cssLink.remove();
      const customStyle = document.getElementById('n8n-custom-styles');
      if (customStyle) customStyle.remove();
    };
  }, []);

  // Open the n8n chat by clicking the hidden trigger
  const handleMascotClick = () => {
    const btn = n8nTriggerRef.current || document.querySelector('.chat-window-toggle');
    if (btn) {
      btn.click();
    } else {
      console.warn('n8n chat trigger not yet available');
    }
  };

  const resetVerification = () => {
    setNotRobot(false);
    setSliderVerified(false);
    setResetKey(prev => prev + 1);
  };

  const handleWhatsAppClick = () => {
    setShowWhatsAppVerify(true);
  };

  const handleCloseVerify = () => {
    setShowWhatsAppVerify(false);
    resetVerification();
  };

  const handleOpenWhatsApp = useCallback(() => {
    if (!isVerified) return;
    const num = getProtectedNumber();
    const msg = encodeURIComponent('¡Hola! Me gustaría información sobre envíos a Latinoamérica.');
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank', 'noopener,noreferrer');
    setShowWhatsAppVerify(false);
    resetVerification();
  }, [isVerified]);

  return (
    <>
      {/* WhatsApp Button - bottom-right (raised above Emergent badge) */}
      <motion.button
        onClick={handleWhatsAppClick}
        className="fixed bottom-20 right-6 z-50 whatsapp-btn w-16 h-16 rounded-full flex items-center justify-center text-white animate-pulse-green shadow-xl"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring" }}
        data-testid="whatsapp-float-btn"
        aria-label="Contactar por WhatsApp"
      >
        <WhatsAppIcon />
      </motion.button>

      {/* WhatsApp Verification Modal */}
      <AnimatePresence>
        {showWhatsAppVerify && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60]"
              onClick={handleCloseVerify}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed bottom-40 right-6 z-[60] w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
              data-testid="whatsapp-verify-modal"
            >
              <div className="bg-[#25D366] p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <WhatsAppIcon />
                  </div>
                  <div className="text-white">
                    <h4 className="font-bold text-sm">WhatsApp</h4>
                    <p className="text-xs text-white/80">Tecno Envios Express</p>
                  </div>
                </div>
                <button 
                  onClick={handleCloseVerify}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src={MASCOT_URL} 
                    alt="Mascota" 
                    className="w-12 h-12 object-contain"
                  />
                  <p className="text-slate-600 text-sm">
                    Verifica que eres humano para continuar al chat
                  </p>
                </div>

                <div className="space-y-4">
                  <label 
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      notRobot 
                        ? 'bg-[#25D366]/5 border-[#25D366]' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="wa-not-robot-container"
                  >
                    <Checkbox 
                      checked={notRobot}
                      onCheckedChange={(checked) => setNotRobot(checked)}
                      className="h-5 w-5 border-2 data-[state=checked]:bg-[#25D366] data-[state=checked]:border-[#25D366]"
                      data-testid="wa-not-robot-checkbox"
                    />
                    <span className="text-slate-700 font-medium text-sm flex-1">
                      No soy un robot
                    </span>
                    {notRobot && (
                      <CheckCircle2 className="w-4 h-4 text-[#25D366]" />
                    )}
                  </label>

                  <SliderCaptcha 
                    key={resetKey}
                    onVerified={setSliderVerified} 
                    verified={sliderVerified}
                    resetKey={resetKey}
                  />

                  <Button
                    onClick={handleOpenWhatsApp}
                    disabled={!isVerified}
                    className={`w-full rounded-xl py-5 font-semibold transition-all flex items-center justify-center gap-2 ${
                      isVerified 
                        ? 'bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid="wa-open-chat"
                  >
                    <WhatsAppIcon />
                    {isVerified ? 'Abrir WhatsApp' : 'Completa la verificación'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Mascot Chatbot Button - bottom-left, opens n8n chat */}
      <motion.button
        onClick={handleMascotClick}
        className="fixed bottom-6 left-6 z-50 w-20 h-20 rounded-full flex items-center justify-center shadow-2xl overflow-hidden bg-gradient-to-br from-[#2DD4BF] to-[#0066CC] p-1 hover:shadow-[0_0_30px_rgba(45,212,191,0.5)] transition-shadow cursor-pointer group"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring" }}
        data-testid="mascot-chat-btn"
        aria-label="Abrir chat con asistente"
      >
        <div className="w-full h-full bg-white rounded-full flex items-center justify-center relative pointer-events-none">
          <img 
            src={MASCOT_URL} 
            alt="Chat con nosotros" 
            className="w-14 h-14 object-contain"
          />
          {/* Chat indicator */}
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#CC0000] rounded-full border-2 border-white flex items-center justify-center">
            <MessageCircle className="w-2.5 h-2.5 text-white fill-white" />
          </span>
        </div>
      </motion.button>

      {/* Tooltip for mascot - only shown until chat is ready and clicked */}
      <motion.div
        className="fixed bottom-28 left-6 z-40 bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg pointer-events-none"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: chatReady ? 1 : 0, y: 0 }}
        transition={{ delay: 2 }}
      >
        ¡Haz clic para chatear!
        <div className="absolute -bottom-1 left-6 w-2 h-2 bg-slate-900 rotate-45" />
      </motion.div>
    </>
  );
};

export default FloatingButtons;
